import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  useTheme,
} from "@mui/material";

const Export: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [exportedUrl, setExportedUrl] = useState("");
  const [exporting, setExporting] = useState(false);
  const theme = useTheme();

  /**
   * Convert external images (tile textures) in the SVG to base64 data
   */
  const inlineImages = (svg: SVGSVGElement): Promise<void> => {
    return new Promise((resolve, reject) => {
      const images = svg.querySelectorAll("image");
      if (images.length === 0) {
        resolve();
        return;
      }
      let processed = 0;
      images.forEach((img) => {
        const href = img.getAttribute("href") || img.getAttribute("xlink:href");
        if (!href) {
          processed++;
          if (processed === images.length) resolve();
          return;
        }
        fetch(href)
          .then((res) => res.blob())
          .then((blob) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const dataUrl = reader.result as string;
              img.setAttribute("href", dataUrl);
              processed++;
              if (processed === images.length) resolve();
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          })
          .catch((err) => {
            console.error("Error inlining image", err);
            processed++;
            if (processed === images.length) resolve();
          });
      });
    });
  };

  /**
   * Gather bounding box for polygons/images to crop out unneeded white space
   */
  const getElementBounds = (elem: SVGGraphicsElement) => {
    // For <image> tags, we can read x/y/width/height. Otherwise fallback to getBBox()
    if (elem.tagName.toLowerCase() === "image") {
      const xAttr = elem.getAttribute("x");
      const yAttr = elem.getAttribute("y");
      const wAttr = elem.getAttribute("width");
      const hAttr = elem.getAttribute("height");
      if (xAttr && yAttr && wAttr && hAttr) {
        const x = parseFloat(xAttr);
        const y = parseFloat(yAttr);
        const w = parseFloat(wAttr);
        const h = parseFloat(hAttr);
        return { x, y, width: w, height: h };
      }
    }
    return elem.getBBox();
  };

  const handleExport = async () => {
    // Immediately open the dialog with a spinner
    setExportedUrl("");
    setExporting(true);
    setOpen(true);

    // Grab the original SVG
    const svgOriginal = document.getElementById("export-svg") as SVGSVGElement | null;
    if (!svgOriginal) {
      console.error("No SVG found with id='export-svg'");
      setExporting(false);
      return;
    }

    // Clone it
    const clone = svgOriginal.cloneNode(true) as SVGSVGElement;

    // Inline external images
    await inlineImages(clone);

    // Remove red selection polygons + force black outlines
    const polygons = clone.querySelectorAll("polygon");
    polygons.forEach((poly) => {
      const stroke = poly.getAttribute("stroke")?.toLowerCase() || "";
      if (stroke.includes("255,87,84") || stroke === "red") {
        poly.remove();
      } else {
        poly.setAttribute("stroke", "black");
        poly.setAttribute("stroke-width", "6");
        poly.setAttribute("stroke-linejoin", "round");
      }
    });

    // Collect bounding box data from polygons and images
    const allPolys = Array.from(clone.querySelectorAll("polygon"));
    const allImgs = Array.from(clone.querySelectorAll("image"));

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    const updateBounds = (elem: SVGGraphicsElement) => {
      const { x, y, width, height } = getElementBounds(elem);
      if (width <= 0 || height <= 0) return;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    };

    allPolys.forEach(updateBounds);
    allImgs.forEach(updateBounds);

    if (minX === Infinity || minY === Infinity) {
      // No content found, fallback to entire original
      const fallbackW = parseFloat(svgOriginal.getAttribute("width") || "800");
      const fallbackH = parseFloat(svgOriginal.getAttribute("height") || "800");
      clone.setAttribute("viewBox", `0 0 ${fallbackW} ${fallbackH}`);
      clone.setAttribute("width", `${fallbackW}`);
      clone.setAttribute("height", `${fallbackH}`);
    } else {
      // Add a bit of padding
      const padding = 10;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;
      const newWidth = maxX - minX;
      const newHeight = maxY - minY;
      // Adjust viewBox
      clone.setAttribute("viewBox", `${minX} ${minY} ${newWidth} ${newHeight}`);
      clone.setAttribute("width", `${newWidth}`);
      clone.setAttribute("height", `${newHeight}`);

      // White background sized to the bounding box
      const bgRect = document.createElementNS(clone.namespaceURI, "rect");
      bgRect.setAttribute("x", "0");
      bgRect.setAttribute("y", "0");
      bgRect.setAttribute("width", `${newWidth}`);
      bgRect.setAttribute("height", `${newHeight}`);
      bgRect.setAttribute("fill", "white");
      clone.insertBefore(bgRect, clone.firstChild);
    }

    // Serialize -> data URL -> draw onto canvas -> finalize
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clone);
    const svgDataUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = svgDataUrl;
    img.onload = () => {
      const w = parseFloat(clone.getAttribute("width") || "800");
      const h = parseFloat(clone.getAttribute("height") || "800");
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        setExporting(false);
        return;
      }
      // White background
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, w, h);
      // Draw
      ctx.drawImage(img, 0, 0);

      // Convert to JPEG
      const jpgDataUrl = canvas.toDataURL("image/jpeg", 1.0);
      setExportedUrl(jpgDataUrl);
      setExporting(false);
    };
    img.onerror = (err) => {
      console.error("Error loading exported SVG as image", err);
      setExporting(false);
    };
  };

  // Download
  const handleDownload = () => {
    if (!exportedUrl) return;
    const link = document.createElement("a");
    link.href = exportedUrl;
    link.download = "map-export.jpg";
    link.click();
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Button variant="contained" onClick={handleExport}>
        Export Map
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Export Preview</DialogTitle>

        {/* 
          Apply the same scrollbar style from your question 
          to this DialogContent or an inner Box. 
        */}
        <DialogContent
          dividers
          sx={{
            position: "relative",
            overflowY: "auto",
            overflowX: "hidden",
            pl: 1,
            direction: "rtl",
            // Same scrollbar style:
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.primary.main,
              borderRadius: "3px",
            },
          }}
        >
          <Box sx={{ direction: "ltr" }}>
            {/* If still exporting or no URL yet, show spinner */}
            {exporting && !exportedUrl ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 300,
                }}
              >
                <CircularProgress color="primary" />
              </Box>
            ) : exportedUrl ? (
              <Box
                component="img"
                src={exportedUrl}
                alt="Map Export Preview"
                sx={{ width: "100%", display: "block", margin: "0 auto" }}
              />
            ) : (
              <Typography>Something went wrong.</Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          {exportedUrl && (
            <Button onClick={handleDownload} color="primary" variant="contained">
              Download JPG
            </Button>
          )}
          <Button onClick={() => setOpen(false)} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Export;